"use client";

import React, { useRef } from "react";
import { STICKY_HEADER_ATTRIBUTE } from "./sticky-header.shared";
import { useStickyScrolled } from "./use-sticky-scrolled";
import styles from "./sticky-header.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * GH5/GH6 — the box that keeps the grid header on screen, and switches it to its scrolled
 * background once the reader has gone past it. The client-boundary argument.
 */

export interface StickyHeaderProps {
  /**
   * GH6 — "if the MyFree DIRECTV banner is present, the header also shows a shadow whenever it has
   * the scrolled background." An explicit boolean rather than `Boolean(bannerSlot)`, because an
   * empty fragment is a perfectly ordinary thing for a caller to pass and it is not a banner.
   */
  hasBanner?: boolean;
  /** Rendered inside the pinned box, already built by the server caller. */
  children: React.ReactNode;
  className?: string;
}

function StickyHeaderImpl({
  hasBanner = false,
  children,
  className
}: StickyHeaderProps) {
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useStickyScrolled(sentinelRef, boxRef);

  return (
    <>
      {/* Zero-size and hidden from assistive tech — it has nothing to say and exists only to be observed. It must sit in normal flow immediately before the pinned box: once it has scrolled out of view, the box below it is stuck. */}
      <span aria-hidden="true" className={styles.sentinel} ref={sentinelRef} />
      <div
        className={[styles.sticky, className].filter(Boolean).join(" ")}
        data-cl-banner={hasBanner ? "true" : "false"}
        data-cl-scrolled="false"
        {...{ [STICKY_HEADER_ATTRIBUTE]: "true" }}
        ref={boxRef}
      >
        {children}
      </div>
    </>
  );
}
export const StickyHeader = withSafeRender(StickyHeaderImpl, "StickyHeader");

export default StickyHeader;
